/**
 * Fullscreen (Options): the page fills the screen, no browser bars. Browsers only enter
 * fullscreen on a tap, so the option is saved and `useFullscreen` re-enters on your next tap
 * after a reload. Leaving fullscreen yourself (Back, Esc, the browser's own control) turns the
 * option off. iPhone browsers have no fullscreen (Add to Home Screen gives the same).
 */
import { useEffect, useRef } from 'react';

export const canFullscreen = (): boolean =>
  typeof document !== 'undefined' && !!document.fullscreenEnabled && !!document.documentElement.requestFullscreen;

const isFullscreen = (): boolean => !!document.fullscreenElement;

/** Call from a tap (the browser refuses otherwise). */
export function enterFullscreen(): void {
  if (!canFullscreen() || isFullscreen()) return;
  document.documentElement.requestFullscreen({ navigationUI: 'hide' }).catch(() => {
    // Refused (no tap, or not allowed here): the next tap tries again.
  });
}

export function exitFullscreen(): void {
  if (isFullscreen()) document.exitFullscreen().catch(() => {});
}

/** Keeps the page fullscreen while `on`: enters on the next tap, and calls `onLeft` when you leave it. */
export function useFullscreen(on: boolean, onLeft: () => void): void {
  const left = useRef(onLeft);
  useEffect(() => {
    left.current = onLeft;
  });
  useEffect(() => {
    if (!on || !canFullscreen()) return;
    const tap = () => enterFullscreen();
    let was = isFullscreen();
    const changed = () => {
      const now = isFullscreen();
      if (was && !now) left.current();
      was = now;
    };
    document.addEventListener('pointerdown', tap, true);
    document.addEventListener('fullscreenchange', changed);
    return () => {
      document.removeEventListener('pointerdown', tap, true);
      document.removeEventListener('fullscreenchange', changed);
    };
  }, [on]);
}
